import { FunctionComponent, memo, useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthorInfo,
  Comments,
  ReactionsCounter,
  getChannelDomain,
  getWebAppUrl,
  layoutPath,
  truncateString,
  useAppDispatch,
  useCardDate,
} from '../../../../';
import { ConfigContext, UserContext } from '../../../../../context';
import { StoryCard, StoryCardEventContent } from '../../../../../services';
import { reactOnCard } from '../../../../../slices';
import { CommentsCounter } from '../../../ActionPane/CommentsCounter';
import { ReactionSelect } from '../../../ActionPane/ReactionSelect';
import { Share } from '../../../ActionPane/Share';
import { FeedCardMargin } from '../../../FeedCard/FeedCardMargin';

import classes from './EventDiscussionCard.module.scss';

interface EventDiscussionCardProps {
  card: StoryCard;
}

export const EventDiscussionCard: FunctionComponent<EventDiscussionCardProps> = memo(({ card }) => {
  const dispatch = useAppDispatch();

  const commentBoxRef = useRef<HTMLTextAreaElement>(null);

  const {
    id: cardId,
    storyId,
    myReaction,
    reactions,
    comments,
    commentsCount,
    commentsRootCount,
    content,
    author,
    story,
    dateToDisplay,
    options: { allowCardReaction, allowComment, allowSharing },
  } = card;

  const { headline } = content as StoryCardEventContent;

  const { t } = useTranslation();

  const date = useCardDate(dateToDisplay);

  const { showPostedTime } = useContext(ConfigContext).config.elements.card;

  const { id: userId } = useContext(UserContext).userProfile;

  const { id: authorId, avatar, screenName: authorName } = { ...author };

  const isAuthor = useMemo(() => Boolean(authorId === userId), [authorId, userId]);

  const storyTitleTrunc = useMemo(
    () => truncateString({ value: story.title, maxLength: 100 }),
    [story.title]
  );

  const headerSubTitle = useMemo(
    () =>
      `${t('feed.posted-in')} "${storyTitleTrunc}"${
        showPostedTime ? `${t('common.separator')} ${date}` : ''
      }`,
    [date, showPostedTime, storyTitleTrunc, t]
  );

  const onAuthorClick = useCallback(
    () =>
      window.open(
        `${getWebAppUrl(getChannelDomain())}${layoutPath(
          `/profile${!isAuthor ? `/${authorId}` : ''}`
        )}`,
        '_blank'
      ),
    [authorId, isAuthor]
  );

  const totalReactionsCount = useMemo(
    () =>
      Object.values(reactions)
        .filter(({ count }) => Boolean(count))
        .reduce((res, { count }) => res + count, 0) as number,
    [reactions]
  );

  const onlyMyReaction = useMemo(
    () => Boolean(myReaction && totalReactionsCount === 1),
    [myReaction, totalReactionsCount]
  );

  const renderReactionsCounter = useMemo(
    () => Boolean(allowCardReaction && totalReactionsCount > 0 && !onlyMyReaction),
    [allowCardReaction, onlyMyReaction, totalReactionsCount]
  );

  const onReactionSelect = useCallback(
    (reaction: string) => {
      dispatch(reactOnCard({ storyId, cardId, reaction }));
    },
    [cardId, dispatch, storyId]
  );

  const onCommentsCounterClick = useCallback(() => {
    if (!commentBoxRef.current) {
      return;
    }

    commentBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    commentBoxRef.current.focus({ preventScroll: true });
  }, []);

  return (
    <div className={classes['event-discussion-card']}>
      <FeedCardMargin top right bottom left>
        <AuthorInfo
          avatarUrl={avatar?.url}
          title={authorName}
          subTitle={headerSubTitle}
          {...(author && { onClick: onAuthorClick })}
        />
      </FeedCardMargin>

      {headline && (
        <FeedCardMargin top right bottom left>
          <div className={classes['event-discussion-card__headline']}>{headline}</div>
        </FeedCardMargin>
      )}

      {renderReactionsCounter && (
        <FeedCardMargin right left>
          <div className={classes['event-discussion-card__reactions-counter']}>
            <ReactionsCounter
              totalCount={totalReactionsCount}
              reactions={reactions}
              myReaction={myReaction}
            />
          </div>
        </FeedCardMargin>
      )}

      <FeedCardMargin top right left>
        <div className={classes['event-discussion-card__action-pane']}>
          {allowCardReaction && (
            <ReactionSelect myReaction={myReaction} onSelect={onReactionSelect} />
          )}

          {allowComment && (
            <CommentsCounter commentsCount={commentsCount} onClick={onCommentsCounterClick} />
          )}

          {allowSharing && <Share storyId={storyId} cardId={cardId} />}
        </div>
      </FeedCardMargin>

      {allowComment && (
        <FeedCardMargin right left bottom>
          <Comments
            ref={commentBoxRef}
            storyId={storyId}
            cardId={cardId}
            comments={comments}
            commentsRootCount={commentsRootCount}
            wrapper={document.querySelector('.modal-content') as HTMLElement}
          />
        </FeedCardMargin>
      )}
    </div>
  );
});
